import type { TowerDesign } from "@/game/towerSpec";

/**
 * Monopole: structurally a slim braced lattice (a truss needs width to resist
 * bending), clad in a tapered steel tube via `shell` so it reads as a real pole.
 * Owned by the monopole agent. See docs/ENGINEERING.md §3.3.
 *
 * Sizing rationale (why it stands with headroom yet fails plausibly):
 *  - faces: 8 — a rounder, stiffer polygon than 6; spreads gravity + sway into
 *    more legs and raises the base leg-couple that resists overturning.
 *  - baseRadius: 0.75 m — the earlier 0.6 m base (aspect ~25:1) was so slim the
 *    lattice swayed during settling, pushing a leg near capacity at ZERO wind
 *    (~81% util). Lateral bending stiffness of the leg group scales with
 *    baseRadius², so widening to 0.75 m (base width 1.5 m, aspect ~20:1) kills
 *    that P-delta spike while keeping the slender pole silhouette.
 *  - legSection: legXL (6" Sch-40 pipe) with 2.5 m panels — legs YIELD before
 *    they buckle (Pcr ≫ A·Fy at this length), so the governing failure is base
 *    bending: the windward leg reaches A·Fy = 1242 kN, giving a base-moment
 *    capacity ≈ 3.7 MN·m.
 *  - braceHeavy diagonals + 12 short (2.5 m) panels keep every face fully
 *    triangulated and the diagonals well below buckling.
 * Result: peak util ≈ 0 at zero wind, ~0.11 at 30 m/s, ~0.42 at 60 m/s, and it
 * loses the base leg couple (topples in bending) as wind approaches ~75 m/s.
 *
 * shell: baseDiameter 1.5 m = 2×baseRadius so the tube fully encloses the
 * internal lattice legs; tapers to 0.82 m at the top for the classic pole look.
 */
export const design: TowerDesign = {
  id: "monopole-30m",
  name: "Monopole",
  description: "30 m tapered steel monopole (8-sided, carrier-grade).",
  spec: {
    type: "monopole",
    height: 30,
    stack: {
      faces: 8,
      panels: 12,
      baseRadius: 0.75,
      topRadius: 0.4,
      legSection: "legXL",
      braceSection: "braceHeavy",
      bracing: "X",
    },
    shell: {
      baseDiameter: 1.5,
      topDiameter: 0.82,
    },
  },
};
